import React, { useState } from "react";
import { X } from "lucide-react";
import { ECourseFormData } from "../../services/elearning/eCourseService";
import toast from "react-hot-toast";

interface CreateCourseModalProps {
  onClose: () => void;
  onSubmit: (courseData: ECourseFormData) => Promise<void>;
  isSubmitting?: boolean;
}

export const CreateCourseModal: React.FC<CreateCourseModalProps> = ({
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState<ECourseFormData>({
    title: "",
    description: "",
    shortDescription: "",
    category: "Other",
    level: "Beginner",
    language: "English",
    duration: {
      estimatedHours: 0,
      estimatedMinutes: 0,
    },
    pricing: {
      type: "free",
      amount: 0,
      currency: "KES",
    },
    registration: {
      type: "self",
      requiresApproval: false,
      maxStudents: undefined,
    },
    settings: {
      allowDiscussions: true,
      allowDownloads: false,
      trackProgress: true,
      generateCertificate: false,
    },
    tags: [],
    visibility: "public",
    chain: {
      chainId: "",
      sequenceNumber: 1,
      isChainFinal: true,
      nextCourseId: "",
    },
  });

  const [tagInput, setTagInput] = useState("");
  const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);
  const [coverPhotoPreview, setCoverPhotoPreview] = useState<string | null>(
    null
  );
  const [isUploading, setIsUploading] = useState(false);

  const categories = [
    "Programming",
    "Design",
    "Business",
    "Marketing",
    "Language",
    "Science",
    "Mathematics",
    "Arts",
    "Health",
    "Technology",
    "Other",
  ];

  const languages = [
    "English",
    "Spanish",
    "French",
    "German",
    "Italian",
    "Portuguese",
    "Chinese",
    "Japanese",
    "Korean",
    "Arabic",
    "Somali",
    "Hindi",
    "Russian",
    "Swahili",
    "Other",
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsUploading(true);
    try {
      let thumbnailUrl = null;

      // Upload cover photo if selected
      if (coverPhotoFile) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", coverPhotoFile);
        formDataUpload.append("folder", "thumbnails");

        const response = await fetch("/api/elearning/upload/file", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formDataUpload,
        });

        if (!response.ok) {
          throw new Error("Upload failed");
        }

        const result = await response.json();
        thumbnailUrl = result.data.url;
      }

      // Call onSubmit with updated form data
      onSubmit({ ...formData, thumbnail: thumbnailUrl });
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload cover photo. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((tag) => tag !== tagToRemove) || [],
    }));
  };

  const handleCoverPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setCoverPhotoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setCoverPhotoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold">Create New E-Course</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Basic Information</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Title *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Enter course title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Short Description
              </label>
              <input
                type="text"
                value={formData.shortDescription}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    shortDescription: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Brief description for course cards"
                maxLength={200}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
              </label>
              <textarea
                required
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Detailed course description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cover Photo
              </label>
              <div className="space-y-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverPhotoChange}
                  disabled={isUploading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
                {isUploading && (
                  <p className="text-sm text-blue-600">Uploading...</p>
                )}
                {coverPhotoPreview && (
                  <div className="relative">
                    <img
                      src={coverPhotoPreview}
                      alt="Cover photo preview"
                      className="w-full h-48 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setCoverPhotoPreview(null);
                        setFormData((prev) => ({
                          ...prev,
                          thumbnail: undefined,
                        }));
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  Recommended: 1200x600px, JPG or PNG format
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Level
                </label>
                <select
                  value={formData.level}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      level: e.target.value as any,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Language
                </label>
                <select
                  value={formData.language}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      language: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  {languages.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Estimated Duration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hours
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.duration?.estimatedHours || ""}
                  placeholder="0"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration: {
                        ...prev.duration!,
                        estimatedHours:
                          e.target.value === ""
                            ? ""
                            : parseInt(e.target.value) || 0,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minutes
                </label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={formData.duration?.estimatedMinutes || ""}
                  placeholder="0"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration: {
                        ...prev.duration!,
                        estimatedMinutes:
                          e.target.value === ""
                            ? ""
                            : parseInt(e.target.value) || 0,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Pricing</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course Type
              </label>
              <select
                value={formData.pricing?.type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    pricing: {
                      ...prev.pricing!,
                      type: e.target.value as "free" | "paid",
                    },
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="free">Free</option>
                <option value="paid">Paid</option>
              </select>
            </div>

            {formData.pricing?.type === "paid" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price (KES)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.pricing?.amount || 0}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      pricing: {
                        ...prev.pricing!,
                        amount:
                          parseFloat(parseFloat(e.target.value).toFixed(2)) ||
                          0,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter price in Kenyan Shillings"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Price in Kenyan Shillings (KES)
                </p>
              </div>
            )}
          </div>

          {/* Registration Settings */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Registration Settings</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Type
              </label>
              <select
                value={formData.registration?.type}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    registration: {
                      ...prev.registration!,
                      type: e.target.value as any,
                    },
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="self">Self Registration</option>
                <option value="manual">Manual Registration</option>
                <option value="invite-only">Invite Only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Students (optional)
              </label>
              <input
                type="number"
                min="1"
                value={formData.registration?.maxStudents || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    registration: {
                      ...prev.registration!,
                      maxStudents: e.target.value
                        ? parseInt(e.target.value)
                        : undefined,
                    },
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Leave empty for unlimited"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="requiresApproval"
                checked={formData.registration?.requiresApproval || false}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    registration: {
                      ...prev.registration!,
                      requiresApproval: e.target.checked,
                    },
                  }))
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label
                htmlFor="requiresApproval"
                className="ml-2 block text-sm text-gray-700"
              >
                Require approval for enrollment
              </label>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Tags</h3>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) =>
                  e.key === "Enter" && (e.preventDefault(), addTag())
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Add tags..."
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Add
              </button>
            </div>

            {formData.tags && formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="ml-1 text-blue-400 hover:text-blue-600"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Visibility */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Visibility</h3>
            <select
              value={formData.visibility}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  visibility: e.target.value as any,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="public">Public - Anyone can see and enroll</option>
              <option value="branch-only">
                Branch Only - Only users from your branch
              </option>
              <option value="private">Private - Only visible to you</option>
            </select>
          </div>

          {/* Course Chain Settings */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">
              Course Series (Optional)
            </h3>
            <p className="text-sm text-gray-600">
              Group this course with others in a sequential learning series.
              Students must complete courses in order.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Series ID
                </label>
                <input
                  type="text"
                  value={formData.chain?.chainId || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      chain: {
                        ...prev.chain!,
                        chainId: e.target.value,
                      },
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., graphic-design-series"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Unique identifier for the course series
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sequence Number
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.chain?.sequenceNumber || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const numValue = value === "" ? "" : parseInt(value) || 1;
                      setFormData((prev) => ({
                        ...prev,
                        chain: {
                          ...prev.chain!,
                          sequenceNumber: numValue,
                        },
                      }));
                    }}
                    placeholder="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Order in the series (1, 2, 3...)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Is Final Course?
                  </label>
                  <select
                    value={formData.chain?.isChainFinal ? "true" : "false"}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        chain: {
                          ...prev.chain!,
                          isChainFinal: e.target.value === "true",
                        },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="false">No - Intermediate course</option>
                    <option value="true">
                      Yes - Final course (issues certificate)
                    </option>
                  </select>
                </div>
              </div>

              {formData.chain?.chainId && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Series Configuration
                  </h4>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p>
                      <strong>Series:</strong> {formData.chain.chainId}
                    </p>
                    <p>
                      <strong>Position:</strong> Part{" "}
                      {formData.chain.sequenceNumber}
                    </p>
                    <p>
                      <strong>Certificate:</strong>{" "}
                      {formData.chain.isChainFinal
                        ? "Issued upon completion"
                        : "Not issued (intermediate course)"}
                    </p>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Students must complete all previous courses in this series
                    before enrolling in this one.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <div className="flex-1 text-sm text-gray-600">
              <p>
                <strong>Note:</strong> Your course will be submitted for admin
                approval before it can be published. You will be notified once
                it's reviewed.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUploading || isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading
                  ? "Uploading..."
                  : isSubmitting
                  ? "Creating..."
                  : "Submit for Approval"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
